import {
  React, useCallback, useEffect, useMemo, useState,
} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { fDateTime } from '../../utils/formatTime';
import CustomPagination from '../CustomPagination';
import BackButton from '../backButton';
import Iconify from '../Iconify';
import { APIGetOperator } from '../../utils/apiCalled';
import Label from '../Label';
import ModalDelete from '../ModalDelete';

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    boxShadow: theme.customShadows?.z8 || '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
  '& fieldset': {
    borderWidth: '1px !important',
    borderColor: `${alpha(theme.palette.grey[500], 0.32)} !important`,
  },
}));

export default function MorePasses({
  selectedPass,
  handleClose,
  handleAssignClick,
  canAssign,
  assignments,
  assignmentsLoading,
  assignmentsError,
  assignmentsPage,
  assignmentsLimit,
  assignmentsTotal,
  onChangeAssignmentsPage,
  assignmentsSearchQuery,
  onAssignmentsSearchChange,
  onUnassign,
  unassigningId,
}) {
  const theme = useTheme();
  const [operatorsMap, setOperatorsMap] = useState({});
  const [unassignModal, setUnassignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Fetch operators to create map
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const result = await APIGetOperator(() => {});
        if (result?.data) {
          const map = {};
          result.data.forEach((op) => {
            map[op._id] = op.operatorName;
          });
          setOperatorsMap(map);
        }
      } catch (error) {
        console.error('Error fetching operators:', error);
      }
    };

    fetchOperators();
  }, []);

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const assignButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.9),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.main,
      color: 'white',
      opacity: 0.6,
    },
  };

  const assignmentRows = useMemo(() => {
    if (!Array.isArray(assignments)) {
      return [];
    }

    const rows = assignments
      .filter((bundle) => bundle?.serviceCode === selectedPass?.serviceCode)
      .map((bundle, index) => {
        const user = bundle.user || {};
        const fullName = user.firstName || user.lastName
          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
          : '';

        // Format usage from applicationLimits
        let usage = '—';
        if (bundle?.applicationLimits && typeof bundle.applicationLimits === 'object') {
          const trek = bundle.applicationLimits.TREK;
          const erl = bundle.applicationLimits.ERL;
          const parts = [];
          if (trek !== undefined && trek !== null) {
            parts.push(`TREK ${trek}`);
          }
          if (erl !== undefined && erl !== null) {
            parts.push(`ERL ${erl}`);
          }
          if (parts.length > 0) {
            usage = parts.join(' | ');
          }
        }

        return {
          key: bundle.purchaseId || bundle.transactionId || `${bundle.serviceCode}-${index}`,
          purchaseId: bundle.purchaseId,
          name: fullName || user.fullName || user.email || '—',
          email: user.email || '—',
          phone: user.phone || '—',
          status: bundle.status || '—',
          verificationStatus: user.verificationStatus || '—',
          expiryDate: bundle.expiryDate,
          assignedAt: bundle.createdAt,
          usage,
        };
      });

    return rows;
  }, [assignments, selectedPass?.serviceCode]);

  const handlePaginationChange = (event, newPage) => {
    if (typeof onChangeAssignmentsPage === 'function') {
      onChangeAssignmentsPage(newPage);
    }
  };
  const handleSearchChange = useCallback((event) => {
    if (typeof onAssignmentsSearchChange === 'function') {
      onAssignmentsSearchChange(event.target.value);
    }
  }, [onAssignmentsSearchChange]);

  return (
    <Box sx={{ mt: 2 }}>
      <BackButton back={handleClose} type="passes" />
      <Box sx={{ mt: 1, pb: 4 }}>
        <Typography variant="h3">{selectedPass?.serviceName}</Typography>
        <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
          {selectedPass?.description || 'No description provided.'}
        </Typography>
      </Box>

      <Box sx={{
        border: '1px solid', borderColor: theme.palette.grey[200], borderRadius: 2, p: 3, mb: 3,
      }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Chip
            label={selectedPass?.status || 'UNKNOWN'}
            color={selectedPass?.status === 'ACTIVE' ? 'success' : 'default'}
            sx={{ fontWeight: 'fontWeightMedium' }}
          />
          <Chip
            label={selectedPass?.serviceProviders || 'N/A'}
            variant="outlined"
            sx={{ fontWeight: 'fontWeightMedium' }}
          />
          {selectedPass?.passType && (
            <Chip
              label={selectedPass.passType}
              variant="outlined"
              sx={{ fontWeight: 'fontWeightMedium' }}
            />
          )}
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Service Code
            </Typography>
            <Typography variant="subtitle1">{selectedPass?.serviceCode || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Price (MYR)
            </Typography>
            <Typography variant="subtitle1">
              {selectedPass?.price !== undefined ? `RM ${Number(selectedPass.price).toFixed(2)}` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Discount
            </Typography>
            <Typography variant="subtitle1">
              {selectedPass?.percentageDiscount !== undefined
                ? `${selectedPass.percentageDiscount}%`
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Validity Period
            </Typography>
            <Typography variant="subtitle1">
              {selectedPass?.validityPeriodDays
                ? `${selectedPass.validityPeriodDays} days`
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Service Type
            </Typography>
            <Typography variant="subtitle1">{selectedPass?.erlServiceType || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
              Assigned Operators
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Array.isArray(selectedPass?.assignedOperators)
                && selectedPass.assignedOperators.length > 0 ? (
                  selectedPass.assignedOperators.map((opId) => (
                    <Label
                      key={opId}
                      variant="soft"
                      color="primary"
                    >
                      {operatorsMap[opId] || 'Unknown'}
                    </Label>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    No Operators Assigned
                  </Typography>
                )}
            </Stack>
          </Grid>
          {selectedPass?.applicationLimits && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
                Application Limits
              </Typography>
              <Stack spacing={1}>
                {Object.entries(selectedPass.applicationLimits).map(([key, value]) => (
                  <Typography key={key} variant="body2">
                    {key}
                    :
                    {value}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>

        {Array.isArray(selectedPass?.termCond) && selectedPass.termCond.length > 0 && (
          <Accordion sx={{
            mt: 3, boxShadow: 'none', border: '1px solid', borderColor: 'grey.200',
          }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Terms &amp; Conditions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {selectedPass.termCond.map((item) => (
                  <Typography key={item.title} variant="body2">
                    <strong>{item.title}</strong>
                    :
                    {item.description}
                  </Typography>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.grey[200],
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Assigned Users</Typography>
        </Stack>
        <SearchStyle
          value={assignmentsSearchQuery || ''}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or phone"
          startAdornment={(
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          )}
          sx={{ mb: 2, width: '50%', maxWidth: 400 }}
        />

        {(() => {
          if (assignmentsLoading) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            );
          }

          if (assignmentsError) {
            return <Alert severity="error">{assignmentsError}</Alert>;
          }

          if (assignmentRows.length > 0) {
            return (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Assigned At</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Usage</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignmentRows.map((row) => (
                      <TableRow key={row.key} hover>
                        <TableCell>{row.name || '—'}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.verificationStatus}</TableCell>
                        <TableCell>{row.assignedAt ? fDateTime(row.assignedAt) : '—'}</TableCell>
                        <TableCell>{row.expiryDate ? fDateTime(row.expiryDate) : '—'}</TableCell>
                        <TableCell>{row.usage}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            disabled={
                              !canAssign
                              || assignmentsLoading
                              || (unassigningId && unassigningId === row.purchaseId)
                            }
                            onClick={() => {
                              if (row.purchaseId) {
                                setSelectedAssignment(row);
                                setUnassignModal(true);
                              }
                            }}
                          >
                            Unassign
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <CustomPagination
                  page={assignmentsPage}
                  totalNum={assignmentsTotal}
                  rowsPerPage={assignmentsLimit}
                  handleChangePage={handlePaginationChange}
                />
              </>
            );
          }

          return <Alert severity="info">No users have been assigned to this pass yet.</Alert>;
        })()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button sx={buttonStyle} onClick={handleClose}>
          <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Close</Typography>
        </Button>
        <Button
          sx={assignButtonStyle}
          onClick={handleAssignClick}
          disabled={!canAssign}
        >
          <Typography sx={{ fontWeight: 'fontWeightMedium' }}>Assign Pass</Typography>
        </Button>
      </Box>

      {unassignModal && selectedAssignment && (
        <ModalDelete
          deleteModal={unassignModal}
          handleDelete={(data) => {
            if (typeof onUnassign === 'function' && data?.purchaseId) {
              onUnassign(data.purchaseId);
            }
            setUnassignModal(false);
            setSelectedAssignment(null);
          }}
          handleClose={() => {
            setUnassignModal(false);
            setSelectedAssignment(null);
          }}
          data={{
            purchaseId: selectedAssignment.purchaseId,
            userName: selectedAssignment.name,
            name: selectedAssignment.name,
          }}
        />
      )}
    </Box>
  );
}
